const monthMap = {
    jan: "January",
    feb: "February",
    mar: "March",
    apr: "April",
    may: "May",
    jun: "June",
    jul: "July",
    aug: "August",
    sep: "September",
    oct: "October",
    nov: "November",
    dec: "December",
};

export function initialiseReportCharts(root = document) {
    root.querySelectorAll(".js-interest-chart").forEach((element) => {
        if (element.dataset.chartBound === "1") {
            return;
        }

        element.dataset.chartBound = "1";
        new InterestChartController(element);
    });

    root.querySelectorAll(".js-seasonality-chart").forEach((element) => {
        if (element.dataset.chartBound === "1") {
            return;
        }

        element.dataset.chartBound = "1";
        new SeasonalityChartController(element);
    });
}

function parseChartConfig(element, selector) {
    const configElement = element.querySelector(selector);
    if (!configElement) {
        return null;
    }

    try {
        return JSON.parse(configElement.textContent);
    } catch (error) {
        console.error("Unable to parse chart config.", error);
        return null;
    }
}

class BaseChartController {
    constructor(root, containerSelector, canvasSelector) {
        this.root = root;
        this.chartContainer = root.querySelector(containerSelector);
        this.canvas = root.querySelector(canvasSelector);
        this.chart = undefined;
        this.resizeObserver = undefined;
        this.resizeAnimationFrame = undefined;
        this.resizeHandler = () => {
            this.queueResize();
        };

        this.registerResizeHandlers();
    }

    registerResizeHandlers() {
        window.addEventListener("resize", this.resizeHandler);

        if (window.ResizeObserver && this.chartContainer) {
            this.resizeObserver = new ResizeObserver(() => {
                this.queueResize();
            });

            this.resizeObserver.observe(this.chartContainer);
        }
    }

    queueResize() {
        if (!this.chart) {
            return;
        }

        if (this.resizeAnimationFrame) {
            cancelAnimationFrame(this.resizeAnimationFrame);
        }

        this.resizeAnimationFrame = requestAnimationFrame(() => {
            this.chart.resize();
        });
    }

    destroyExistingChart() {
        if (!this.canvas || !window.Chart) {
            return;
        }

        const existingChart = window.Chart.getChart(this.canvas);
        if (existingChart !== undefined) {
            existingChart.destroy();
        }
    }
}

class InterestChartController extends BaseChartController {
    constructor(root) {
        super(root, ".js-interest-chart-container", ".js-interest-canvas");

        this.config = parseChartConfig(root, ".js-interest-chart-config");
        this.select = root.querySelector(".js-interest-period-picker");
        this.toggleButtons = Array.from(
            root.querySelectorAll(".js-interest-toggle"),
        );
        this.growthValue = root.querySelector(".js-interest-growth-value");
        this.growthIconContainer = root.querySelector(
            ".js-interest-growth-icon-container",
        );
        this.activeChart = Number(this.config?.defaultChart ?? 0);
        this.hiddenIndexes = {};

        if (!this.config || !this.canvas) {
            return;
        }

        this.bindEvents();
        this.updateToggleButtons();
        this.updateGrowthUi();
        this.renderChart();
    }

    bindEvents() {
        this.select?.addEventListener("change", (event) => {
            this.activeChart = Number(event.target.value);
            this.renderChart();
        });

        this.toggleButtons.forEach((button) => {
            button.addEventListener("click", (event) => {
                event.preventDefault();

                const index = Number(button.dataset.datasetIndex);
                this.hiddenIndexes[index] = !(
                    this.hiddenIndexes[index] ?? false
                );

                this.updateToggleButtons();
                this.renderChart();
            });
        });
    }

    currentChart() {
        return (
            this.config?.charts?.[this.activeChart] ??
            this.config?.charts?.[0] ??
            null
        );
    }

    buildDatasets() {
        return (this.currentChart()?.datasets ?? []).map((dataset, index) => {
            return {
                ...dataset,
                hidden: this.hiddenIndexes[index] ?? false,
            };
        });
    }

    activeTrendMaximum() {
        return (
            this.currentChart()?.trendMaximum ??
            this.config?.productTrendMaximum ??
            0
        );
    }

    renderChart() {
        const chartDefinition = this.currentChart();
        if (!chartDefinition || !this.canvas) {
            return;
        }

        const trendMaximum = this.activeTrendMaximum();
        const xaxis = chartDefinition.xaxis ?? [];
        const activeChartIndex = this.activeChart;

        this.destroyExistingChart();
        this.chart = new window.Chart(this.canvas, {
            type: "line",
            data: {
                labels: xaxis,
                datasets: this.buildDatasets(),
            },
            options: {
                responsive: true,
                animation: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? false : undefined,
                resizeDelay: 100,
                legend: {
                    display: false,
                },
                scales: {
                    x: {
                        grid: {
                            display: false,
                        },
                        ticks: {
                            autoSkip: false,
                            callback(value, idx, ticks) {
                                if (idx === 0 || idx === ticks.length - 1) {
                                    return xaxis[idx];
                                }

                                if (activeChartIndex === 0) {
                                    return xaxis[idx];
                                }

                                if (activeChartIndex === 1 && idx % 3 === 0) {
                                    return xaxis[idx];
                                }

                                if (activeChartIndex === 2 && idx % 6 === 0) {
                                    return xaxis[idx];
                                }

                                return "";
                            },
                            maxRotation: 0,
                        },
                    },
                    y: {
                        border: {
                            display: false,
                        },
                        title: {
                            display: false,
                        },
                        ticks: {
                            display: true,
                            callback(value, idx, ticks) {
                                if (value === 0) {
                                    return "0";
                                }

                                if (idx === ticks.length - 1) {
                                    return "100";
                                }

                                return "";
                            },
                        },
                        min: 0,
                        suggestedMax: trendMaximum,
                    },
                },
                plugins: {
                    tooltip: {
                        displayColors: false,
                        callbacks: {
                            label(ctx) {
                                let value = (ctx.parsed.y / trendMaximum) * 100;
                                let amountText = "<5%";

                                if (value > 80) {
                                    amountText = ">80%";
                                } else if (value > 20) {
                                    value = Math.round(value / 10) * 10;
                                    amountText = `~${value}%`;
                                } else if (value > 5) {
                                    value = Math.round(value / 5) * 5;
                                    amountText = `~${value}%`;
                                }

                                return `${amountText} of globally top-selling product performance`;
                            },
                        },
                    },
                },
            },
        });

        this.updateGrowthUi();
    }

    updateToggleButtons() {
        this.toggleButtons.forEach((button) => {
            const index = Number(button.dataset.datasetIndex);
            const isActive = !(this.hiddenIndexes[index] ?? false);

            button.classList.toggle("active", isActive);
            button.setAttribute("aria-pressed", isActive ? "true" : "false");
        });
    }

    updateGrowthUi() {
        const pchange = this.currentChart()?.pchange;

        if (!this.growthValue || !this.growthIconContainer) {
            return;
        }

        this.growthValue.classList.remove(
            "sh-text-product-green",
            "negative-chart-value",
        );

        if (typeof pchange !== "number") {
            this.growthValue.textContent = "";
            this.growthIconContainer.innerHTML = "";
            return;
        }

        const isPositive = pchange > 0;
        this.growthValue.textContent = `${pchange.toFixed(2)}%`;
        this.growthValue.classList.add(
            isPositive ? "sh-text-product-green" : "negative-chart-value",
        );

        const iconUrl = isPositive
            ? this.config.positiveIcon
            : this.config.negativeIcon;

        this.growthIconContainer.innerHTML = `<img src="${iconUrl}" class="ms-1 js-interest-growth-icon" alt="${isPositive ? "Increase" : "Decrease"}">`;
    }
}

class SeasonalityChartController extends BaseChartController {
    constructor(root) {
        super(root, ".js-seasonality-chart-container", ".js-seasonality-canvas");

        this.config = parseChartConfig(root, ".js-seasonality-chart-config");
        this.toggleButtons = Array.from(
            root.querySelectorAll(".js-seasonality-toggle"),
        );
        this.hiddenIndexes = {};

        if (!this.config || !this.canvas) {
            return;
        }

        this.bindEvents();
        this.updateToggleButtons();
        this.renderChart();
    }

    bindEvents() {
        this.toggleButtons.forEach((button) => {
            button.addEventListener("click", (event) => {
                event.preventDefault();

                const index = Number(button.dataset.datasetIndex);
                this.hiddenIndexes[index] = !(
                    this.hiddenIndexes[index] ?? false
                );

                this.updateToggleButtons();
                this.renderChart();
            });
        });
    }

    buildDatasets() {
        const datasets = (this.config?.datasets ?? []).map((dataset, index) => {
            return {
                ...dataset,
                hidden: this.hiddenIndexes[index] ?? false,
            };
        });

        datasets.push({
            label: "Average Interest",
            data: Array(12).fill(1.0),
            borderColor: "#AAAAAA",
            pointStyle: false,
            borderDash: [5, 5],
        });

        return datasets;
    }

    renderChart() {
        if (!this.canvas) {
            return;
        }

        this.destroyExistingChart();
        this.chart = new window.Chart(this.canvas, {
            type: "line",
            data: {
                labels: this.config?.xaxis ?? [],
                datasets: this.buildDatasets(),
            },
            options: {
                responsive: true,
                animation: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? false : undefined,
                resizeDelay: 100,
                legend: {
                    display: false,
                },
                scales: {
                    x: {
                        grid: {
                            display: false,
                        },
                        ticks: {
                            maxRotation: 0,
                        },
                    },
                    y: {
                        border: {
                            display: false,
                        },
                        title: {
                            display: false,
                        },
                        ticks: {
                            display: true,
                            callback(value, idx, ticks) {
                                if (value === 0) {
                                    return "Lower";
                                }

                                if (value === 1) {
                                    return "Average";
                                }

                                if (idx === ticks.length - 1) {
                                    return "Higher";
                                }

                                return "";
                            },
                        },
                        min: 0,
                        suggestedMax: 2,
                    },
                },
                plugins: {
                    tooltip: {
                        displayColors: false,
                        filter(ctx) {
                            return ctx.datasetIndex === 0;
                        },
                        callbacks: {
                            title(ctx) {
                                if (ctx && ctx[0]) {
                                    return monthMap[ctx[0].label.toLowerCase()];
                                }

                                return "";
                            },
                            label(ctx) {
                                let value = (ctx.parsed.y - 1.0) * 100;
                                let absoluteValue = Math.ceil(Math.abs(value));
                                let direction = "lower";

                                if (value >= 0) {
                                    direction = "higher";
                                }

                                let amountText = "A little (<5%)";
                                if (absoluteValue > 50) {
                                    absoluteValue =
                                        Math.round(absoluteValue / 10) * 10;
                                    amountText = `Significantly (~${absoluteValue}%)`;
                                } else if (absoluteValue > 20) {
                                    absoluteValue =
                                        Math.round(absoluteValue / 10) * 10;
                                    amountText = `Somewhat (~${absoluteValue}%)`;
                                } else if (absoluteValue > 5) {
                                    absoluteValue =
                                        Math.round(absoluteValue / 5) * 5;
                                    amountText = `Slightly (~${absoluteValue}%)`;
                                }

                                return `${amountText} ${direction} vs month-to-month average interest`;
                            },
                        },
                    },
                },
            },
        });
    }

    updateToggleButtons() {
        this.toggleButtons.forEach((button) => {
            const index = Number(button.dataset.datasetIndex);
            const isActive = !(this.hiddenIndexes[index] ?? false);

            button.classList.toggle("active", isActive);
            button.setAttribute("aria-pressed", isActive ? "true" : "false");
        });
    }
}

